/**
 * Central catalog for user-facing playback / data loading errors.
 *
 * GoBrain surfaces every failure through a single `error` widget event. The payload
 * carries the failing URL and (for network failures) the HTTP status code, but it does
 * not tell us *what kind of resource* failed. This module classifies the failure into
 * one of three sources the product spec defines messages for, then maps
 * (source, httpStatus) to a translation key plus a human readable error code.
 *
 *   HLS-Playlist  the .m3u8 manifest could not be loaded
 *   HLS-Segment   a media segment (.ts / .m4s / .aac ...) could not be loaded
 *   API           the /medias, /playlists or /scenarios endpoint failed
 */
export namespace ErrorCatalog {
	export const SOURCES = {
		hlsPlaylist: 'HLS-Playlist',
		hlsSegment: 'HLS-Segment',
		api: 'API',
	} as const;

	export type ErrorSource = typeof SOURCES[keyof typeof SOURCES];

	/** Statuses the spec defines a dedicated message for. */
	export const MAPPED_STATUSES = [400, 401, 403, 404, 500, 503, 524] as const;

	export type MappedStatus = typeof MAPPED_STATUSES[number];

	/** i18n key for the "Error code: <source> <status>" line rendered under the message. */
	export const ERROR_CODE_LABEL = 'ERROR_CODE_LABEL';

	const KEY_PREFIX: Record<ErrorSource, string> = {
		[SOURCES.hlsPlaylist]: 'ERROR_MESSAGE_HLS_PLAYLIST_',
		[SOURCES.hlsSegment]: 'ERROR_MESSAGE_HLS_SEGMENT_',
		[SOURCES.api]: 'ERROR_MESSAGE_API_',
	};

	/**
	 * Collapse an arbitrary HTTP status onto one of the seven statuses the spec covers.
	 * Returns null when the status carries no useful meaning (0 / undefined / 2xx), so
	 * callers can fall back to the legacy generic messages.
	 */
	export const normalizeStatus = (status: unknown): MappedStatus | null => {
		const code = typeof status === 'string' ? parseInt(status, 10) : status;
		if (typeof code !== 'number' || !Number.isFinite(code) || code < 400) {
			return null;
		}

		if ((MAPPED_STATUSES as readonly number[]).includes(code)) {
			return code as MappedStatus;
		}

		// Timeouts share the 524 wording: 408 Request Timeout, 504 Gateway Timeout,
		// 598/599 network read/connect timeout.
		if ([408, 504, 598, 599].includes(code)) {
			return 524;
		}
		// Cloudflare 52x other than 524, plus every other 5xx, read as a service error.
		if (code === 502) {
			return 503;
		}
		if (code >= 500) {
			return 500;
		}
		// Remaining 4xx (405, 410, 429, ...) read as a bad request.
		return 400;
	};

	const SEGMENT_EXTENSIONS = ['.ts', '.m4s', '.aac', '.mp4', '.m4a', '.m4v', '.cmfv', '.cmfa', '.vtt'];

	/**
	 * Work out which of the three error sources a failing URL belongs to.
	 * Returns null when the URL is absent or unrecognised.
	 */
	export const classifyUrl = (url: unknown): ErrorSource | null => {
		if (typeof url !== 'string' || url === '') {
			return null;
		}

		// Strip query string and fragment before looking at the extension: HLS URLs are
		// routinely signed with tokens such as ?token=...&expires=...
		const path = url.split(/[?#]/)[0].toLowerCase();

		if (path.endsWith('.m3u8')) {
			return SOURCES.hlsPlaylist;
		}
		if (path.endsWith('.mpd')) {
			// DASH manifests fail the same way and read the same to a viewer.
			return SOURCES.hlsPlaylist;
		}
		if (SEGMENT_EXTENSIONS.some((extension) => path.endsWith(extension))) {
			return SOURCES.hlsSegment;
		}
		if (/\/(medias|playlists|scenarios|settings|configurations)(\/|$)/.test(path)) {
			return SOURCES.api;
		}

		return null;
	};

	/**
	 * videojs-http-streaming tags every request it makes with a `requestType`, which GoBrain
	 * forwards. It is authoritative, so it wins over sniffing the URL — a signed playlist URL
	 * does not always end in .m3u8.
	 */
	const REQUEST_TYPES: Record<string, ErrorSource> = {
		'content-steering-manifest': SOURCES.hlsPlaylist,
		'dash-manifest': SOURCES.hlsPlaylist,
		'hls-playlist': SOURCES.hlsPlaylist,
		'dash-sidx': SOURCES.hlsSegment,
		segment: SOURCES.hlsSegment,
		'segment-key': SOURCES.hlsSegment,
		'segment-media-initialization': SOURCES.hlsSegment,
	};

	export const classifyRequestType = (requestType: unknown): ErrorSource | null =>
		typeof requestType === 'string' ? REQUEST_TYPES[requestType] ?? null : null;

	export interface ClassifiedError {
		source: ErrorSource;
		status: MappedStatus;
		/** i18n key of the message to show, e.g. ERROR_MESSAGE_HLS_PLAYLIST_404. */
		messageKey: string;
		/** Code rendered to the viewer, e.g. "HLS-Playlist 404". */
		displayCode: string;
	}

	/**
	 * Build the message key / display code for a failing request. Returns null when the
	 * resource or the status cannot be classified, in which case the caller should keep
	 * using the pre-existing generic messages.
	 */
	/**
	 * A 403 from /medias can mean several different things, and the response body is the only
	 * place that says which. GoBrain's restriction mapper only understands four of them
	 * (`ip`, `publish`, `tooEarly`, `tooLate` -> restriction/assets/js/mappers/restriction-mapper.js),
	 * so a geo block or a domain block arrives with no specific restriction attached — and, when
	 * the body also carries `publish`, gets mislabelled as "still being processed". We therefore
	 * read the body ourselves rather than changing the mapper.
	 */
	export const FORBIDDEN_REASONS = {
		geo: 'geo',
		domain: 'domain',
	} as const;

	export type ForbiddenReason = typeof FORBIDDEN_REASONS[keyof typeof FORBIDDEN_REASONS];

	/**
	 * Body keys that mark the 403 as a geo / domain block. The body is a flat map of booleans, one
	 * per failed check: `{"location": true}` for geo and `{"domain": true}` for domain, the same
	 * way an IP block is `{"ip": true}`. All three confirmed against the live /medias endpoint.
	 *
	 * An unrecognised body falls through to the existing AccessForbidden wording, so a key the
	 * platform adds later degrades rather than misleads.
	 */
	export const FORBIDDEN_REASON_KEYS: Record<ForbiddenReason, readonly string[]> = {
		[FORBIDDEN_REASONS.geo]: ['location'],
		[FORBIDDEN_REASONS.domain]: ['domain'],
	};

	// Presence alone is not enough, the flag has to be set — matching how GoBrain's mapper tests
	// `ip` and `publish`. Guards against a body that reports a check as explicitly passed.
	const isSet = (value: unknown): boolean => {
		if (Array.isArray(value)) {
			return value.length > 0;
		}
		if (typeof value === 'string') {
			return value.trim() !== '';
		}
		return Boolean(value);
	};

	/**
	 * Work out whether a 403 response body describes a geo or a domain block.
	 * Accepts either the raw response text or an already-parsed object.
	 * Returns null when the body says nothing about either.
	 */
	export const classifyForbidden = (response: unknown): ForbiddenReason | null => {
		let body: any = response;
		if (typeof response === 'string') {
			try {
				body = JSON.parse(response);
			} catch (error) {
				return null;
			}
		}
		if (!body || typeof body !== 'object') {
			return null;
		}

		// Geo is checked first: a media can fail both checks at once, and the viewer can act on
		// neither, so the order only needs to be stable.
		const reasons: ForbiddenReason[] = [FORBIDDEN_REASONS.geo, FORBIDDEN_REASONS.domain];

		return reasons.find((reason) => FORBIDDEN_REASON_KEYS[reason].some((key) => isSet(body[key]))) ?? null;
	};

	export const classify = ({ url, status, requestType }: { url: unknown; status: unknown; requestType?: unknown }): ClassifiedError | null => {
		const source = classifyRequestType(requestType) ?? classifyUrl(url);
		const normalizedStatus = normalizeStatus(status);
		if (!source || normalizedStatus === null) {
			return null;
		}

		return {
			source,
			status: normalizedStatus,
			messageKey: KEY_PREFIX[source] + normalizedStatus,
			displayCode: `${source} ${normalizedStatus}`,
		};
	};
}
